/**
 * Booking-phase labels, colours and ordering (#82), plus the workspace view
 * derivation (#83, #340 item 5).
 *
 * Phase is derived server-side by the `bookings_with_phase` view and shipped
 * on every payload as `derived_phase`. Both surfaces read that one source: the
 * events list uses the display surface here — labels, colours, ordering, the
 * BookingPhase type — and the workspace maps the same server Phase to each
 * section's state via `deriveWorkspaceView` below. It does NOT re-derive Phase
 * from raw facts; doing so drifted from the view and hid the crew section (#81).
 *
 * This is the canonical booking-phase display logic for the admin SPA.
 */

import { isBalanceDone, isDepositDone } from "./payment-summary";
import badge from "./status-badge.module.css";

export type BookingPhase =
  | "inquiry"
  | "quote"
  | "approved"
  | "deposit"
  | "crew"
  | "event"
  | "balance"
  | "payout"
  | "done"
  | "cancelled";

export const PHASE_LABELS: Record<BookingPhase, string> = {
  inquiry: "Inquiry",
  quote: "Quote Sent",
  approved: "Approved",
  deposit: "Deposit Paid",
  crew: "Crew Notified",
  event: "Crew Confirmed",
  balance: "Balance Invoiced",
  payout: "Paying Crew",
  done: "Done",
  cancelled: "Cancelled",
};

/**
 * One tone class per Phase, from `status-badge.module.css` (#443). The
 * colour, not the shape: a call site composes it onto whatever it is drawing
 * — `badge.badge` for the pill the events list and the workspace header use,
 * the Schedule's own bar class for the timeline. Ten distinguishable steps is
 * why the middle of the ladder reaches past the semantic ramps.
 */
export const PHASE_COLORS: Record<BookingPhase, string> = {
  inquiry: badge.yellow,
  quote: badge.blue,
  approved: badge.green,
  deposit: badge.emerald,
  crew: badge.purple,
  event: badge.indigo,
  balance: badge.orange,
  payout: badge.amber,
  done: badge.green,
  cancelled: badge.red,
};

/** Phases offered in the filter dropdown, in lifecycle order (cancelled last). */
export const PHASE_ORDER: BookingPhase[] = [
  "inquiry",
  "quote",
  "approved",
  "deposit",
  "crew",
  "event",
  "balance",
  "payout",
  "done",
];

/** The empty string is the "All Phases" sentinel. */
export type PhaseFilter = BookingPhase | "";

const ALL_PHASES = new Set<string>([...PHASE_ORDER, "cancelled"]);

/** Narrow an arbitrary string to a known BookingPhase, or null. */
export function asBookingPhase(value: unknown): BookingPhase | null {
  return typeof value === "string" && ALL_PHASES.has(value)
    ? (value as BookingPhase)
    : null;
}

/** Narrow a select value to a PhaseFilter (empty string = All Phases). */
export function isPhaseFilter(v: string): v is PhaseFilter {
  return v === "" || ALL_PHASES.has(v);
}

// ─── Workspace view derivation (#83, consolidated by #340 item 5) ───────────
// One pure derivation answers "which lifecycle phase is this booking in, which
// workspace section is active/complete/future, and is each generated artifact
// stale". The Phase itself is the server's — both the events list and the
// workspace read the bookings_with_phase derivation (#81); the workspace maps
// it to section states plus the section editors' live dirty flags below.

export type SectionState = "active" | "complete" | "future";

/** What one workspace accordion section needs to render its frame. */
export interface SectionView {
  state: SectionState;
  /**
   * The section's generated artifact no longer matches its inputs — the
   * stored generation snapshot's projection differs from the current read's,
   * or a contributing section holds unsaved edits (summary-staleness.ts).
   * Always false for sections without an artifact.
   */
  stale: boolean;
}

export interface WorkspaceSections {
  lineItems: SectionView;
  quoteSummary: SectionView;
  jobDetails: SectionView;
  jobSummary: SectionView;
  crew: SectionView;
  clientPayment: SectionView;
  contractorPayment: SectionView;
}

export interface WorkspaceView {
  /** Real lifecycle phase — the server's derived_phase; controls workspace flow. */
  phase: BookingPhase;
  /** Operator-facing phase label. May be a display override that expires server-side. */
  displayPhase: BookingPhase;
  step: number; // -1 cancelled, else 0..8
  sections: WorkspaceSections;
  depositDone: boolean;
  balanceDone: boolean;
  balanceInvoiceNeedsReissue: boolean;
}

export interface WorkspaceViewInputs {
  booking: {
    cancelled_at?: string | null;
    quote_summary_generated_at?: string | null;
    job_summary_generated_at?: string | null;
    // The real lifecycle Phase, derived server-side by the bookings_with_phase
    // view — the same source the events list badge reads. The workspace uses
    // this rather than re-deriving Phase from raw facts (see deriveWorkspaceView).
    derived_phase?: BookingPhase | null;
    display_phase?: string | null;
  };
  approval?: {
    sent_to_client_at?: string | null;
    payment_status?: string | null;
    balance_status?: string | null;
    balance_invoiced_at?: string | null;
  } | null;
  assignments?: Array<{
    status: string;
    payment_status?: string | null;
  }> | null;
  hasLineItems: boolean;
  // From paymentSummary — deliberately NOT approval.balance_status: the
  // optimistic balance-invoice mask patches paymentSummary only, so the
  // payment sections react to it while the phase waits for the server.
  depositStatus: string;
  balanceStatus: string;
  depositAmount: number;
  /**
   * Client-derived staleness for the two generated artifacts:
   * project(stored generation snapshot) !== project(current read) OR a
   * contributing section is dirty (see summary-staleness.ts). Both projection
   * operands come from the same server payload, so they cannot drift; the
   * dirty flag spans until the post-save refetch lands.
   */
  clientStale: { quoteSummary: boolean; jobSummary: boolean };
}

const STEP_BY_PHASE: Record<BookingPhase, number> = {
  cancelled: -1,
  inquiry: 0,
  quote: 1,
  approved: 2,
  deposit: 3,
  crew: 4,
  event: 5,
  balance: 6,
  payout: 7,
  done: 8,
};

function parseTimestampMs(value: string | null | undefined): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

export function deriveBalanceInvoiceNeedsReissue({
  booking,
  approval,
  balanceStatus,
  clientStale,
}: WorkspaceViewInputs): boolean {
  if (!approval) return false;
  if (clientStale.quoteSummary) return false;
  if (balanceStatus !== "invoiced") return false;

  const quoteGeneratedMs = parseTimestampMs(booking.quote_summary_generated_at);
  if (quoteGeneratedMs == null) return false;

  const balanceInvoicedMs = parseTimestampMs(approval.balance_invoiced_at);
  if (balanceInvoicedMs == null) return true;

  return quoteGeneratedMs > balanceInvoicedMs;
}

/**
 * Derive the whole workspace view — booking phase, each accordion section's
 * visual state, and per-artifact staleness — from server facts + the section
 * editors' dirty flags. The single SPA-side answer to "what state is this
 * booking in"; nothing downstream recomputes or patches it up.
 */
export function deriveWorkspaceView(
  inputs: WorkspaceViewInputs,
): WorkspaceView {
  const {
    booking,
    approval,
    assignments,
    hasLineItems,
    depositStatus,
    balanceStatus,
    depositAmount,
    clientStale,
  } = inputs;

  // The real lifecycle Phase is the server's — the bookings_with_phase view,
  // the same source the events list badge reads. The workspace used to
  // re-derive it here from raw facts and had drifted: the client required both
  // contractors_notified_at AND client_approved_at for `crew`, while the view
  // asks only for contractors_notified_at, so a notified Booking read `crew` in
  // the list and fell through to `quote` in the workspace — hiding the crew
  // section behind a phase that said crew work had not started (#81).
  const phase =
    (booking.cancelled_at
      ? "cancelled"
      : asBookingPhase(booking.derived_phase)) ?? "inquiry";
  const displayPhase = asBookingPhase(booking.display_phase) ?? phase;
  const step = STEP_BY_PHASE[phase];

  const jobPrepActive = step >= 2 && step <= 4;
  const jobPrepPast = step > 4;

  const lineItems: SectionState = step <= 1 ? "active" : "complete";

  const quoteSummary: SectionState = hasLineItems
    ? step <= 1
      ? "active"
      : "complete"
    : "future";

  const jobDetails: SectionState = jobPrepActive
    ? "active"
    : jobPrepPast
      ? "complete"
      : "future";

  // Complete once the booking moves past Job Prep (step > 4), matching the
  // Job Prep group tick and the jobDetails section. Renotify / regenerate /
  // reprint stay available on a 'complete' section, so flipping the border
  // does not remove any operator action.
  const jobSummary: SectionState =
    step >= 5
      ? "complete"
      : step >= 3 || booking.job_summary_generated_at != null
        ? "active"
        : "future";

  const crew: SectionState = jobPrepActive
    ? "active"
    : jobPrepPast
      ? "complete"
      : "future";

  // Settled-ness is payment-summary.ts's derivation — one source, imported,
  // never re-derived here (sweep 2 D6; previously a byte-identical copy).
  const depositDone = isDepositDone(depositStatus, depositAmount);
  const balanceDone = isBalanceDone(balanceStatus);
  const balanceInvoiceNeedsReissue = deriveBalanceInvoiceNeedsReissue(inputs);

  const clientPayment: SectionState =
    depositDone && balanceDone ? "complete" : approval ? "active" : "future";

  const acceptedAssignments = (assignments || []).filter(
    (a) => a.status === "accepted",
  );
  const hasAccepted = acceptedAssignments.length > 0;
  const contractorPayment: SectionState =
    hasAccepted && acceptedAssignments.every((a) => a.payment_status === "paid")
      ? "complete"
      : hasAccepted
        ? "active"
        : "future";

  // Staleness is computed upstream as one pure synchronous derivation
  // (computeWorkspaceStaleness) and passed in. The generated-at guard already
  // lives there, so these flow straight through.
  const quoteSummaryStale = clientStale.quoteSummary;
  const jobSummaryStale = clientStale.jobSummary;

  return {
    phase,
    displayPhase,
    step,
    sections: {
      lineItems: { state: lineItems, stale: false },
      quoteSummary: { state: quoteSummary, stale: quoteSummaryStale },
      jobDetails: { state: jobDetails, stale: false },
      jobSummary: { state: jobSummary, stale: jobSummaryStale },
      crew: { state: crew, stale: false },
      clientPayment: { state: clientPayment, stale: false },
      contractorPayment: { state: contractorPayment, stale: false },
    },
    depositDone,
    balanceDone,
    balanceInvoiceNeedsReissue,
  };
}
